/**
 * ISO 3166-1 alpha-2 codes. Names are resolved through `Intl.DisplayNames` rather than hardcoded so
 * there is one list to maintain, and the display locale is pinned to English because the app itself
 * is English-only — a country name saved on a billing address must not shift with the browser locale.
 */
const COUNTRY_CODES = `
	AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ
	BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ
	CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ
	DE DJ DK DM DO DZ EC EE EG EH ER ES ET
	FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY
	HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT
	JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ
	LA LB LC LI LK LR LS LT LU LV LY
	MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ
	NA NC NE NF NG NI NL NO NP NR NU NZ OM
	PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW
	SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ
	TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ
	UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW
`
	.trim()
	.split(/\s+/);

/** ISO code for India — the only country with a dedicated tax and address flow today. */
export const INDIA_COUNTRY_CODE = "IN";

/** English display name for India, as stored on billing addresses. */
export const INDIA_COUNTRY_NAME = "India";

const displayNames =
	typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
		? new Intl.DisplayNames(["en"], { type: "region" })
		: null;

/** English name for an alpha-2 code, falling back to the code itself on unsupported browsers. */
export const getCountryName = (code: string): string => {
	try {
		return displayNames?.of(code.toUpperCase()) ?? code.toUpperCase();
	} catch {
		return code.toUpperCase();
	}
};

/** Every country name, alphabetically — the option list for a country selector. */
export const COUNTRY_NAMES: string[] = COUNTRY_CODES.map(getCountryName).sort((left: string, right: string) =>
	left.localeCompare(right, "en")
);

const CODE_BY_NAME: Record<string, string> = COUNTRY_CODES.reduce(
	(map: Record<string, string>, code: string) => ({ ...map, [getCountryName(code).toLowerCase()]: code }),
	{}
);

/** Alpha-2 code for an English country name, or null when the name is not a recognised country. */
export const getCountryCodeByName = (name: string): string | null => CODE_BY_NAME[name.trim().toLowerCase()] ?? null;

/** True when the saved country name is India. Anything unrecognised counts as outside India. */
export const isIndiaCountryName = (name: string): boolean =>
	name.trim().toLowerCase() === INDIA_COUNTRY_NAME.toLowerCase();
